import * as THREE from 'three'
import type { AudioFrame } from '../types'

/**
 * "From inside" camera control (look around) for the wrap-around LED shapes
 * (sphere / cylinder). The camera sits at the center and the view rotates with
 * yaw/pitch:
 *  - by dragging the pointer (manual), or
 *  - with a gentle auto-pan + beat shake (cinematic).
 */
export class InsideLook {
  private lon = 0 // yaw
  private lat = 0 // pitch
  private dragging = false
  private px = 0
  private py = 0
  private dom: HTMLElement
  private dir = new THREE.Vector3()
  enabled = false

  constructor(dom: HTMLElement) {
    this.dom = dom
    dom.addEventListener('pointerdown', this.onDown)
    window.addEventListener('pointermove', this.onMove)
    window.addEventListener('pointerup', this.onUp)
  }

  private onDown = (e: PointerEvent) => {
    if (!this.enabled) return
    this.dragging = true
    this.px = e.clientX
    this.py = e.clientY
  }

  private onMove = (e: PointerEvent) => {
    if (!this.dragging || !this.enabled) return
    this.lon -= (e.clientX - this.px) * 0.0026
    this.lat = THREE.MathUtils.clamp(this.lat + (e.clientY - this.py) * 0.0026, -1.45, 1.45)
    this.px = e.clientX
    this.py = e.clientY
  }

  private onUp = () => {
    this.dragging = false
  }

  update(camera: THREE.PerspectiveCamera, frame: AudioFrame, dt: number, cinematic: boolean) {
    camera.position.set(0, 0, 0)
    camera.up.set(0, 1, 0)
    if (cinematic) {
      this.lon += dt * 0.07
      this.lat += (Math.sin(performance.now() * 0.0002) * 0.35 - this.lat) * 0.02
    }
    const cl = Math.cos(this.lat)
    const s = frame.beatIntensity * 0.02
    this.dir.set(
      Math.sin(this.lon) * cl + (Math.random() * 2 - 1) * s,
      Math.sin(this.lat) + (Math.random() * 2 - 1) * s,
      Math.cos(this.lon) * cl,
    )
    camera.lookAt(this.dir) // camera at the origin -> looks in this direction
  }

  dispose() {
    this.dom.removeEventListener('pointerdown', this.onDown)
    window.removeEventListener('pointermove', this.onMove)
    window.removeEventListener('pointerup', this.onUp)
  }
}
